import { useState, useCallback, useRef, type ReactNode } from 'react';

/**
 * The four-box shape answer pad — spades-hearts-diamonds-clubs (feature 028).
 *
 * Restyled onto the 023 table-session idiom. The answer FORMAT is untouched and must stay so:
 * the four values are joined with hyphens, the first is required, and the rest default to "0".
 * The verdict is a trimmed string comparison against the hand's stored answer, so a change to
 * how this string is built changes what counts as correct.
 *
 * Accessibility additions over the pre-028 pad: each box now carries its own `aria-label` (the
 * suit symbols above were decorative and announced nothing), the error is wired to the first box
 * with `aria-describedby`, and the symbols are hidden from assistive technology since the labels
 * now carry the same information as text.
 */

interface HandShapeInputProps {
  onSubmit: (answer: string) => void;
  disabled: boolean;
  /**
   * A second action rendered in Submit's own row (feature 028: the page's "Skip Hand"). The row
   * is a column everywhere but phones, so a caller that passes nothing — and every caller that
   * did before — gets exactly the button it had. See `.counting-answer-actions` in index.css.
   */
  trailingAction?: ReactNode;
}

const SUIT_CONFIG = [
  { symbol: '♠', color: 'text-black', name: 'Spades' },
  { symbol: '♥', color: 'text-red-600', name: 'Hearts' },
  { symbol: '♦', color: 'text-red-600', name: 'Diamonds' },
  { symbol: '♣', color: 'text-black', name: 'Clubs' },
];

const BTN =
  'w-full rounded-lg py-3 text-center text-[15px] font-bold tracking-wide transition-colors ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ' +
  'disabled:cursor-not-allowed disabled:opacity-60 xnarrow:py-2 padswap:py-2';

export function HandShapeInput({ onSubmit, disabled, trailingAction }: HandShapeInputProps) {
  const [values, setValues] = useState(['', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = useCallback((index: number, rawValue: string) => {
    if (rawValue !== '' && !/^\d+$/.test(rawValue)) return;

    setValues(prev => {
      const next = [...prev];
      next[index] = rawValue;
      return next;
    });
    setError(null);

    if (rawValue && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  }, []);

  const handleSubmit = useCallback(() => {
    if (!values[0]?.trim()) {
      setError('First value (Spades) is required.');
      inputRefs.current[0]?.focus();
      return;
    }

    // Format preserved exactly: first value as typed, the rest defaulting to "0", hyphen-joined.
    const answer = values.map((v, i) => {
      if (i === 0) return v.trim();
      return v.trim() || '0';
    }).join('-');

    onSubmit(answer);
  }, [values, onSubmit]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      return;
    }
    if (e.key === 'Enter') {
      handleSubmit();
      return;
    }
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  }, [handleSubmit]);

  return (
    <div className="space-y-3 xnarrow:space-y-2 padswap:space-y-2" data-testid="hand-shape-input">
      {/* Suit symbols. Decorative now that each box names its own suit — hidden from assistive
          technology so the shape is not announced twice. */}
      <div className="flex justify-center flex-nowrap" aria-hidden="true">
        {SUIT_CONFIG.map((suit, idx) => (
          <div key={`s${idx}`} className="flex items-center flex-shrink-0">
            {idx > 0 && <span className="text-transparent text-sm mx-0.5">-</span>}
            <div className="w-11 text-center">
              <span className={`${suit.color} font-suits text-xl`}>{suit.symbol}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-center flex-nowrap">
        {SUIT_CONFIG.map((suit, idx) => (
          <div key={`i${idx}`} className="flex items-center flex-shrink-0">
            {idx > 0 && <span className="text-ink-soft text-sm mx-0.5" aria-hidden="true">-</span>}
            <input
              ref={(el) => { inputRefs.current[idx] = el; }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={values[idx]}
              onChange={(e) => handleChange(idx, e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              placeholder={idx === 0 ? '' : '0'}
              aria-label={`${suit.name} count`}
              aria-invalid={error != null && idx === 0}
              aria-describedby={error && idx === 0 ? 'hand-shape-error' : undefined}
              // 16px keeps iOS Safari from zooming on focus; the pre-028 pad dropped to 14px on
              // short viewports and paid for it with a scroll-restore workaround on the page.
              className={`w-11 rounded-lg border bg-card px-2 py-2.5 text-center text-[16px] font-semibold text-ink
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600
                disabled:bg-cream disabled:text-ink-soft
                xnarrow:w-10 xnarrow:px-1 padswap:py-1.5
                ${error && idx === 0 ? 'border-danger' : 'border-line-strong'}`}
              autoFocus={idx === 0}
            />
          </div>
        ))}
      </div>

      {error && (
        <p id="hand-shape-error" role="alert" className="text-center text-[12px] text-danger">
          {error}
        </p>
      )}

      <div className="counting-answer-actions">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={disabled}
          data-testid="counting-submit"
          className={`${BTN} bg-primary-600 text-white shadow-[0_2px_0_#063d21] hover:bg-primary-700 focus-visible:ring-primary-600`}
        >
          Submit
        </button>
        {trailingAction}
      </div>
    </div>
  );
}
